// src/views/Client/ClientLayout.js

import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  ChatBubbleLeftEllipsisIcon,
  UserCircleIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { UserProvider, useUser } from '../../context/UserContext'; // 👈 importar aquí
import { getClientConversation } from '../../api/backend';

// ✅ ENVOLVEMOS el layout con el Provider
const ClientLayout = () => {
  return (
    <UserProvider>
      <LayoutWithUser />
    </UserProvider>
  );
};

// ✅ Layout original separado como componente interno
const LayoutWithUser = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { clientProfile } = useUser(); // ✅ Ahora sí funciona
  const [unreadMessages, setUnreadMessages] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { to: 'dashboard', label: 'Panel', icon: HomeIcon },
    { to: 'invoices', label: 'Facturas', icon: DocumentTextIcon },
    { to: 'messages', label: 'Mensajes', icon: ChatBubbleLeftEllipsisIcon },
    { to: 'service', label: 'Servicio', icon: WrenchScrewdriverIcon },
    { to: 'profile', label: 'Perfil', icon: UserCircleIcon },
  ];

  useEffect(() => {
    let cancelled = false;

    const loadConversationState = async () => {
      try {
        const response = await getClientConversation();
        if (cancelled) return;
        setUnreadMessages(response.unreadCount || 0);
      } catch (error) {
        if (!cancelled) {
          console.warn('No se pudo obtener el estado de mensajes del cliente:', error);
        }
      }
    };

    loadConversationState();
    const intervalId = window.setInterval(loadConversationState, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  const isMessagesView = location.pathname.includes('/clients/messages');

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gray-50">

      {/* Topbar móvil */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b">
        <div className="flex items-center gap-3">
          <img src="/logo-bbts.png" alt="Logo" className="w-20" />
          {clientProfile?.profileImageUrl && (
            <img
              src={clientProfile.profileImageUrl}
              alt="Avatar"
              className="w-10 h-10 rounded-full object-cover border border-gray-300"
            />
          )}
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? (
            <XMarkIcon className="h-6 w-6 text-gray-700" />
          ) : (
            <Bars3Icon className="h-6 w-6 text-gray-700" />
          )}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`bg-white w-full md:w-64 border-r shadow-sm md:block ${
          sidebarOpen ? 'block' : 'hidden'
        }`}
      >
        <div className="p-4 flex flex-col items-center">

          <img
            src="/logo-bbts.png"
            alt="Logo"
            className="w-28 mb-4"
          />

          {clientProfile && (
            <div className="mb-6 text-center">
              <img
                src={clientProfile.profileImageUrl || '/default-avatar.png'}
                alt="Avatar"
                className="w-20 h-20 rounded-full object-cover border-2 border-gray-300 mx-auto mb-2"
              />
              <p className="text-sm font-medium text-gray-800">
                {clientProfile.businessName || 'Cliente'}
              </p>
              <p className="text-xs text-gray-500 break-all">{clientProfile.email}</p>
            </div>
          )}

          <nav className="space-y-2 w-full">
            {navItems.map((item) => (
              <NavItem
                key={item.to}
                {...item}
                unreadCount={item.to === 'messages' ? unreadMessages : 0}
              />
            ))}
          </nav>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-4 space-y-6">
        <div className="overflow-x-auto flex gap-3 py-2 px-1">
          {navItems.map((item) => (
            <TabLink
              key={item.to}
              {...item}
              unreadCount={item.to === 'messages' ? unreadMessages : 0}
            />
          ))}
        </div>
        <Outlet />
      </main>

      {!isMessagesView && (
        <button
          type="button"
          onClick={() => navigate('/clients/messages')}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-3 rounded-2xl bg-blue-600 px-4 py-3 text-left text-white shadow-[0_18px_45px_rgba(37,99,235,0.35)] transition hover:bg-blue-700"
        >
          <div className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-white/14 ring-1 ring-white/20">
            <ChatBubbleLeftEllipsisIcon className="h-6 w-6" />
            {unreadMessages > 0 && (
              <span className="absolute -right-1 -top-1 flex min-h-[1.4rem] min-w-[1.4rem] items-center justify-center rounded-full bg-amber-400 px-1 text-[0.7rem] font-bold text-slate-900">
                {unreadMessages > 9 ? '9+' : unreadMessages}
              </span>
            )}
          </div>
          <div className="hidden sm:block">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-100">Support Desk</p>
            <p className="text-sm font-semibold">Mensajeria en vivo</p>
          </div>
        </button>
      )}
    </div>
  );
};

const NavItem = ({ to, label, icon: Icon, unreadCount = 0 }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `flex items-center justify-between gap-3 px-4 py-2 rounded text-sm font-medium transition ${
        isActive
          ? 'bg-blue-100 text-blue-700'
          : 'text-gray-700 hover:bg-gray-100'
      }`
    }
  >
    <span className="flex items-center gap-3">
      <Icon className="h-5 w-5" />
      <span>{label}</span>
    </span>
    {unreadCount > 0 && (
      <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[0.7rem] font-semibold text-slate-900">
        {unreadCount > 9 ? '9+' : unreadCount}
      </span>
    )}
  </NavLink>
);

const TabLink = ({ to, label, unreadCount = 0 }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-full border transition text-sm font-medium ${
        isActive
          ? 'bg-blue-600 text-white border-blue-600 shadow'
          : 'bg-gray-100 text-gray-700 border-transparent hover:bg-blue-100'
      }`
    }
  >
    {label}
    {unreadCount > 0 && (
      <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[0.7rem] font-semibold text-slate-900">
        {unreadCount > 9 ? '9+' : unreadCount}
      </span>
    )}
  </NavLink>
);

export default ClientLayout;
